import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { Moment } from 'moment';
// Api
import {
  CREATE_TICKET_PRESALE_CODE,
  SET_TICKET_PRESALE_CODE_ACTIVE,
} from 'api/ticketing/mutations';
// Types
import {
  CreateTicketPresaleCode,
  CreateTicketPresaleCodeVariables,
} from 'api/ticketing/types/CreateTicketPresaleCode';
import {
  SetTicketPresaleCodeActive,
  SetTicketPresaleCodeActiveVariables,
} from 'api/ticketing/types/SetTicketPresaleCodeActive';
// Helpers
import { toVenueInstant } from 'pages/Events/helpers';
import { runTicketingChange } from 'pages/Events/runTicketingChange';

export type CreatePresaleFormValues = {
  code: string;
  label: string;
  tierId?: string;
  window: [Moment, Moment];
  maxUses?: number | null;
};

type PresaleEvent = { id: string; timezone: string };
type PresaleCode = { id: string; code: string };

export type PresaleCodeActions = {
  /** The window is read as venue time. Resolves to whether the code was created. */
  createCode: (values: CreatePresaleFormValues) => Promise<boolean>;
  setCodeActive: (code: PresaleCode, active: boolean) => Promise<boolean>;
  creating: boolean;
};

export const usePresaleCodeActions = (
  event: PresaleEvent
): PresaleCodeActions => {
  const { id: ticketEventId, timezone } = event;
  const [createMutation, { loading: creating }] = useMutation<
    CreateTicketPresaleCode,
    CreateTicketPresaleCodeVariables
  >(CREATE_TICKET_PRESALE_CODE);
  const [activeMutation] = useMutation<
    SetTicketPresaleCodeActive,
    SetTicketPresaleCodeActiveVariables
  >(SET_TICKET_PRESALE_CODE_ACTIVE);

  const createCode = useCallback(
    (values: CreatePresaleFormValues): Promise<boolean> =>
      runTicketingChange(
        () =>
          createMutation({
            variables: {
              input: {
                ticketEventId,
                code: values.code.trim().toUpperCase(),
                label: values.label.trim(),
                tierId: values.tierId || null,
                startsAt: toVenueInstant(values.window[0], timezone),
                endsAt: toVenueInstant(values.window[1], timezone),
                maxUses: values.maxUses || null,
              },
            },
          }),
        `Presale code ${values.code.toUpperCase()} created`,
        'Failed to create the code'
      ),
    [createMutation, ticketEventId, timezone]
  );

  const setCodeActive = useCallback(
    (code: PresaleCode, active: boolean): Promise<boolean> =>
      runTicketingChange(
        () =>
          activeMutation({
            variables: { input: { presaleCodeId: code.id, active } },
          }),
        `${code.code} ${active ? 'is active again' : 'is turned off'}`,
        'Failed to update the code'
      ),
    [activeMutation]
  );

  return { createCode, setCodeActive, creating };
};
